import { useMemo, useState, type ReactElement } from 'react'
import { Page, TDrawer } from '@components'
import { findNextPlace, toDisplayAwards, useTranslation } from '@front/model'
import { isDefined, isNotEmpty, type GameAward, type PrizeId } from '@terros/common'
import { CompetitionPageContainer } from '../competition/CompetitionPageContainer'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { AwardSection } from './AwardSection'
import { NoPrizes } from './NoPrizes'
import { PrizeForm } from './PrizeForm'
import { createFileRoute } from '@tanstack/react-router'

function CompetitionPrizes(): ReactElement {
  const { t } = useTranslation()
  const { refetch, isFetching } = useGameContext()

  return (
    <CompetitionPageContainer>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Prizes')} />
      <PrizesPage />
    </CompetitionPageContainer>
  )
}

const PrizesPage = (): ReactElement => {
  const { t } = useTranslation()
  const { data } = useGameContext()
  const awards = data.game.awards
  const [addingPlace, setIsAddingPlace] = useState<number | null>(null)
  const [selectedPrizeId, setSelectedPrizeId] = useState<PrizeId | null>(null)

  const handleSave = (): void => {
    setIsAddingPlace(null)
    setSelectedPrizeId(null)
  }

  const awardsExist = isNotEmpty(awards)

  const showPlace = data.game.gameType !== 'raffle'

  const selected = useMemo(() => {
    const award = awards.find((award) => award.prizes.some((prize) => prize.prizeId === selectedPrizeId))
    if (!award) return
    const prize = award?.prizes.find((prize) => prize.prizeId === selectedPrizeId)
    return { prize, place: award.place }
  }, [awards, selectedPrizeId])

  const displayAwards: GameAward[] = useMemo(() => toDisplayAwards(awards), [awards])

  return (
    <>
      <TDrawer opened={addingPlace !== null} title={t('Add Prize')} onClose={() => setIsAddingPlace(null)}>
        <PrizeForm place={addingPlace ?? findNextPlace(awards, showPlace)} onEndEdit={handleSave} />
      </TDrawer>
      <TDrawer opened={isDefined(selectedPrizeId)} title={t('Edit Prize')} onClose={() => setSelectedPrizeId(null)}>
        {selected && <PrizeForm place={selected.place} prize={selected.prize} onEndEdit={handleSave} />}
      </TDrawer>
      {awardsExist ? (
        <Page.Content scrollArea>
          {displayAwards.map((award) => (
            <AwardSection
              award={award}
              key={award.place}
              onAddPrize={() => setIsAddingPlace(award.place)}
              onEditPrize={(prizeId) => setSelectedPrizeId(prizeId)}
            />
          ))}
        </Page.Content>
      ) : (
        <NoPrizes onAdd={() => setIsAddingPlace(1)} />
      )}
    </>
  )
}

export const Route = createFileRoute('/competition_/$gameId/prize/')({
  component: CompetitionPrizes,
})
